import { useState, useEffect } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { formatUTCToTimezone } from 'common/utils/utils';
import { Flex, Padding } from 'components/base/layout-components';
import Dropdown from 'components/base/Dropdown';
import TimePicker from 'components/base/TimePicker';
import Text from 'components/base/Text';
import { getColor } from 'common/constants/_colors';
import { SPACES, LINE_HEIGHT } from 'common/constants/sizes';
import {
  WORK_DAYS_OPTIONS,
  TIME_PICKER_FORMAT,
  TRACKING
} from 'common/constants/constants';
import * as TrackingService from 'common/services/tracking.service';
import Calendar from 'images/icons/calendar.svg';
import Clock from 'images/icons/clock.svg';
import { LBButton } from '@linearb/buzz';

const CustomizeItemBox = styled(Flex)`
  ${({ showBorder }) =>
    showBorder ? `border-bottom: 2px solid ${getColor('catskillWhite')}` : {}};
`;

const CustomizeImg = styled.img`
  width: 5rem;
`;

const CustomizationPopover = ({
  updateSetting,
  currentTeam,
  workingDays,
  dailyTime,
  closeModal
}) => {
  const [customizationOptions, setCustomizationOptions] = useState({
    workingDays,
    originDailyTime: dailyTime,
    dailyTime,
    displayDailyTime: formatUTCToTimezone(dailyTime, TIME_PICKER_FORMAT)
  });
  const timePickerOnChange = (time, dbTime) => {
    setCustomizationOptions({
      ...customizationOptions,
      dailyTime: dbTime,
      displayDailyTime: time
    });
  };

  useEffect(() => {
    TrackingService.logEvent(TRACKING.EVENTS.VISIT, {
      page: TRACKING.PAGES.PULSE,
      page_tab: TRACKING.PAGE_TABS.TIME_SETTINGS_DIALOG
    });
    saveSettings();
  }, []); // eslint-disable-line

  const saveSettings = async () => {
    try {
      await updateSetting(
        'workingDays',
        customizationOptions['workingDays'],
        'team',
        currentTeam.id
      );
      await updateSetting(
        'dailyTime',
        customizationOptions['dailyTime'],
        'team',
        currentTeam.id
      );
      await updateSetting(
        'timeZone',
        moment.tz.guess(),
        'team',
        currentTeam.id
      );
    } catch (e) {}
  };

  return (
    <Flex direction='column' padding='medium'>
      <Text size='x-large' pb={SPACES['x-big']}>
        Tell us more about how you work
      </Text>
      <CustomizeItemBox direction='row' pb='medium' mb='medium' showBorder>
        <Flex items='center' pr='medium'>
          <CustomizeImg src={Clock} />
        </Flex>
        <Flex items='start' direction='column'>
          <Text size='large' pb={SPACES.small}>
            What time is your daily meeting?
          </Text>
          <Flex items='center' direction='row' pb='small'>
            <TimePicker
              time={customizationOptions.displayDailyTime}
              onChange={timePickerOnChange}
            ></TimePicker>
          </Flex>
          <Text size='medium' lineHeight={LINE_HEIGHT.medium}>
            we'll help you prep for the meeting by providing you the right info
            right on time
          </Text>
        </Flex>
      </CustomizeItemBox>
      <CustomizeItemBox direction='row' pb='x-medium'>
        <Flex items='center' pr='medium'>
          <CustomizeImg src={Calendar} />
        </Flex>
        <Flex items='start' direction='column'>
          <Text size='large' pb={SPACES.small}>
            What are your work days?
          </Text>
          <Padding b='small'>
            <Dropdown
              options={WORK_DAYS_OPTIONS}
              selectedValue={customizationOptions.workingDays}
              onChange={selected => {
                setCustomizationOptions({
                  ...customizationOptions,
                  workingDays: selected
                });
              }}
            />
          </Padding>
          <Text size='medium' lineHeight={LINE_HEIGHT.medium}>
            we want to make your timeline more accurate, and to help you spot
            burnout
          </Text>
        </Flex>
      </CustomizeItemBox>
      <Padding b='small'>
        <Text color='shuttleGray'>
          you can always update the details in the settings / general tab
        </Text>
      </Padding>
      <Flex justify='flex-end'>
        <LBButton
          variant='outline'
          onClick={() => {
            TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
              page: TRACKING.PAGES.PULSE,
              page_tab: TRACKING.PAGE_TABS.TIME_SETTINGS_DIALOG,
              click_type: TRACKING.CLICK.SKIP,
              selected_daily_time: customizationOptions.originDailyTime
            });
            closeModal();
          }}
        >
          Skip
        </LBButton>
        <Padding l='small'>
          <LBButton
            onClick={() => {
              TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
                page: TRACKING.PAGES.PULSE,
                page_tab: TRACKING.PAGE_TABS.TIME_SETTINGS_DIALOG,
                click_type: TRACKING.CLICK.UPDATE,
                selected_daily_time: customizationOptions.dailyTime
              });
              saveSettings(customizationOptions);
              closeModal();
            }}
          >
            Update
          </LBButton>
        </Padding>
      </Flex>
    </Flex>
  );
};

export default CustomizationPopover;
